import { useCallback, useEffect, useState } from "react";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Modal } from "../../primitives/Modal";
import { cn } from "../../utils/cn";
import {
  listSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  listSupplierContacts,
  createSupplierContact,
  type Supplier,
  type SupplierCreate,
  type SupplierContact,
  type SupplierContactCreate,
} from "../../../api/suppliers";
import { BACKEND_INVENTORY_UNAVAILABLE_MSG } from "../../../api/auth";

/** Pays proposés dans le formulaire fournisseur (par défaut Canada). */
const COUNTRY_OPTIONS: { value: string; label: string }[] = [
  { value: "CA", label: "Canada" },
  { value: "US", label: "États-Unis" },
];

/** Provinces canadiennes (code ISO). */
const PROVINCES_CA: { value: string; label: string }[] = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "Colombie-Britannique" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "Nouveau-Brunswick" },
  { value: "NL", label: "Terre-Neuve-et-Labrador" },
  { value: "NS", label: "Nouvelle-Écosse" },
  { value: "NT", label: "Territoires du Nord-Ouest" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Île-du-Prince-Édouard" },
  { value: "QC", label: "Québec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

/** États américains (code ISO). */
const STATES_US: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

type InventoryFournisseursProps = {
  selectedSupplierId: string | null;
};

export function InventoryFournisseurs({ selectedSupplierId }: InventoryFournisseursProps) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleError, setModuleError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [panelSupplierId, setPanelSupplierId] = useState<string | null>(selectedSupplierId);
  const [panelSupplier, setPanelSupplier] = useState<Supplier | null>(null);
  const [contacts, setContacts] = useState<SupplierContact[]>([]);
  const [addContactOpen, setAddContactOpen] = useState(false);

  useEffect(() => {
    setPanelSupplierId(selectedSupplierId);
  }, [selectedSupplierId]);

  const fetchSuppliers = useCallback(() => {
    setLoading(true);
    listSuppliers({
      status: statusFilter || undefined,
      query: search.trim() || undefined,
      page_size: 200,
    })
      .then((list) => {
        setSuppliers(list);
        setModuleError(null);
      })
      .catch((err) => {
        setSuppliers([]);
        setModuleError(err instanceof Error ? err.message : null);
      })
      .finally(() => setLoading(false));
  }, [statusFilter, search]);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      listSuppliers({
        status: statusFilter || undefined,
        query: search.trim() || undefined,
        page_size: 200,
      })
        .then((list) => {
          if (!cancelled) {
            setSuppliers(list);
            setModuleError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setSuppliers([]);
            setModuleError(err instanceof Error ? err.message : null);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      window.clearTimeout(timer);
      cancelled = true;
    };
  }, [statusFilter, search]);

  useEffect(() => {
    if (!panelSupplierId) {
      setPanelSupplier(null);
      setContacts([]);
      return;
    }
    let cancelled = false;
    getSupplier(panelSupplierId)
      .then((s) => {
        if (!cancelled) setPanelSupplier(s);
      })
      .catch(() => {
        if (!cancelled) setPanelSupplier(null);
      });
    listSupplierContacts(panelSupplierId)
      .then((list) => {
        if (!cancelled) setContacts(list);
      })
      .catch(() => {
        if (!cancelled) setContacts([]);
      });
    return () => { cancelled = true; };
  }, [panelSupplierId]);

  const filtered = suppliers.filter(
    (s) =>
      !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-1 min-h-0">
      <div className={cn("flex flex-col min-w-0", panelSupplierId ? "w-1/2 lg:w-2/3" : "flex-1")}>
        <div className="p-6 space-y-4">
          {moduleError === BACKEND_INVENTORY_UNAVAILABLE_MSG && (
            <div className="rounded-md border border-warning bg-warning/10 p-4 text-sm text-warning">
              {moduleError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="text-xl font-semibold text-text">Fournisseurs</h1>
            <div className="flex gap-2">
              <Button label="Rafraîchir" intent="default" size="sm" onClick={fetchSuppliers} disabled={loading} />
              <Button label="Créer un fournisseur" intent="primary" size="sm" onClick={() => setCreateModalOpen(true)} />
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[220px]">
              <Input label="Recherche" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, courriel..." />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="archived">Archivé</option>
            </select>
          </div>

          {loading ? (
            <p className="text-sm text-muted">Chargement...</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-muted">
                    <th className="text-left p-3 font-medium text-text">Nom</th>
                    <th className="text-left p-3 font-medium text-text">Courriel</th>
                    <th className="text-left p-3 font-medium text-text">Téléphone</th>
                    <th className="text-left p-3 font-medium text-text">Statut</th>
                    <th className="text-right p-3 font-medium text-text">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-muted">
                        {suppliers.length === 0 ? (
                          <>
                            Aucun fournisseur. Les produits du catalogue peuvent avoir un fournisseur principal (ex. Green Aero Tech). Essayez <strong>Rafraîchir</strong>, vérifiez le filtre « Tous les statuts », ou exécutez le script <code className="text-xs bg-surface-muted px-1 rounded">ensure_green_aero_supplier</code> sur le backend.
                          </>
                        ) : (
                          "Aucun fournisseur ne correspond à la recherche. Créez un fournisseur ou modifiez les critères."
                        )}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((s) => (
                      <tr
                        key={s.id}
                        className={cn(
                          "border-b border-border hover:bg-surface-muted/50 cursor-pointer",
                          panelSupplierId === s.id && "bg-primary/5"
                        )}
                        onClick={() => {
                          setPanelSupplierId(s.id);
                          window.location.hash = `#/inventories/fournisseurs/${s.id}`;
                        }}
                      >
                        <td className="p-3 font-medium text-text">{s.name}</td>
                        <td className="p-3 text-muted">{s.email || "—"}</td>
                        <td className="p-3 text-muted">{s.phone || "—"}</td>
                        <td className="p-3">{s.status}</td>
                        <td className="p-3 text-right">
                          <button
                            type="button"
                            className="text-primary hover:underline text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPanelSupplierId(s.id);
                              window.location.hash = `#/inventories/fournisseurs/${s.id}`;
                            }}
                          >
                            Détail
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {panelSupplierId && (
        <div className="w-full lg:w-1/3 xl:w-[420px] border-l border-border bg-surface flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold text-text">Détail fournisseur</h2>
            <button
              type="button"
              className="text-muted hover:text-text text-sm"
              onClick={() => {
                setPanelSupplierId(null);
                window.location.hash = "#/inventories/fournisseurs";
              }}
            >
              Fermer
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {panelSupplier && (
              <>
                <div className="space-y-2">
                  <p className="font-medium text-text">{panelSupplier.name}</p>
                  {panelSupplier.email && <p className="text-sm text-muted">{panelSupplier.email}</p>}
                  {panelSupplier.phone && <p className="text-sm text-muted">{panelSupplier.phone}</p>}
                  {panelSupplier.address_line1 && (
                    <p className="text-sm text-muted">
                      {panelSupplier.address_line1}
                      {panelSupplier.city && `, ${panelSupplier.city}`}
                      {panelSupplier.province && ` ${panelSupplier.province}`}
                      {panelSupplier.postal_code && ` ${panelSupplier.postal_code}`}
                    </p>
                  )}
                  <p className="text-xs">
                    Statut : <span className="font-medium">{panelSupplier.status}</span>
                  </p>
                  {panelSupplier.note && <p className="text-sm text-muted mt-2">{panelSupplier.note}</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-text">Contacts</h3>
                    <Button label="Ajouter" intent="default" size="sm" onClick={() => setAddContactOpen(true)} />
                  </div>
                  <ul className="space-y-2">
                    {contacts.map((c) => (
                      <li key={c.id} className="rounded border border-border p-2 text-sm">
                        <span className="font-medium">{c.first_name} {c.last_name}</span>
                        {c.is_primary && <span className="ml-1 text-xs text-primary">(principal)</span>}
                        {c.role && <span className="block text-muted">{c.role}</span>}
                        {c.email && <span className="block text-muted">{c.email}</span>}
                        {c.phone && <span className="block text-muted">{c.phone}</span>}
                      </li>
                    ))}
                    {contacts.length === 0 && <p className="text-sm text-muted">Aucun contact.</p>}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <CreateSupplierModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          setCreateModalOpen(false);
          listSuppliers({ page_size: 200 }).then(setSuppliers);
        }}
      />
      {panelSupplierId && (
        <AddContactModal
          open={addContactOpen}
          onClose={() => setAddContactOpen(false)}
          supplierId={panelSupplierId}
          onCreated={() => {
            setAddContactOpen(false);
            listSupplierContacts(panelSupplierId).then(setContacts);
          }}
        />
      )}
    </div>
  );
}

function CreateSupplierModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<SupplierCreate>({
    name: "",
    email: "",
    phone: "",
    address_line1: "",
    city: "",
    province: "",
    postal_code: "",
    country: "CA",
    status: "active",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const provinceOptions = form.country === "US" ? STATES_US : PROVINCES_CA;
  const provinceLabel = form.country === "US" ? "État" : "Province";

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Le nom du fournisseur est requis.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await createSupplier({
        name: form.name.trim(),
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        address_line1: form.address_line1?.trim() || null,
        address_line2: form.address_line2?.trim() || null,
        city: form.city?.trim() || null,
        province: form.province?.trim() || null,
        postal_code: form.postal_code?.trim() || null,
        country: form.country?.trim() || "CA",
        status: form.status || "active",
        note: form.note?.trim() || null,
      });
      setForm({ name: "", email: "", phone: "", address_line1: "", city: "", province: "", postal_code: "", country: "CA", status: "active", note: "" });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Créer un fournisseur" open={open} onClose={onClose} showDefaultButtons={false} showCloseButton size="lg">
      <div className="space-y-4">
        <Input label="Nom du fournisseur" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Obligatoire" />
        <Input label="Courriel" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Optionnel" />
        <Input label="Téléphone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optionnel" />
        <Input label="Adresse" value={form.address_line1 || ""} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} placeholder="Ligne 1" />
        <Input label="Ville" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <div>
          <label className="block text-sm font-medium text-muted mb-1">Pays</label>
          <select
            value={form.country || "CA"}
            onChange={(e) => {
              const country = e.target.value as "CA" | "US";
              setForm({ ...form, country, province: "" });
            }}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-text"
          >
            {COUNTRY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-muted mb-1">{provinceLabel}</label>
            <select
              value={form.province || ""}
              onChange={(e) => setForm({ ...form, province: e.target.value || null })}
              className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-text"
            >
              <option value="">— Sélectionner —</option>
              {provinceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <Input label="Code postal" value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder={form.country === "US" ? "ZIP" : "A1A 1A1"} />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted mb-1">Statut</label>
          <select
            value={form.status || "active"}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-text"
          >
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="archived">Archivé</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-muted mb-1">Note (optionnel)</label>
          <textarea
            className="w-full min-h-[60px] rounded-md border border-border bg-surface px-3 py-2 text-sm"
            value={form.note || ""}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Notes internes"
          />
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} />
          <Button label={saving ? "Enregistrement…" : "Enregistrer"} intent="primary" size="sm" state={saving ? "loading" : "default"} onClick={handleSubmit} />
        </div>
      </div>
    </Modal>
  );
}

function AddContactModal({
  open,
  onClose,
  supplierId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<SupplierContactCreate>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role: "",
    is_primary: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      setError("Prénom et nom sont requis.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await createSupplierContact(supplierId, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        role: form.role?.trim() || null,
        is_primary: form.is_primary ?? false,
        notes: form.notes?.trim() || null,
      });
      setForm({ first_name: "", last_name: "", email: "", phone: "", role: "", is_primary: false, notes: "" });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Ajouter un contact" open={open} onClose={onClose} showDefaultButtons={false} showCloseButton>
      <div className="space-y-3">
        <Input label="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        <Input label="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        <Input label="Courriel" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Téléphone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Input label="Poste / Rôle" value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="ex. ventes, support" />
        <label className="flex items-center gap-2 text-sm text-text">
          <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} className="rounded border-border" />
          Contact principal
        </label>
        <div>
          <label className="block text-sm font-medium text-muted mb-1">Commentaire</label>
          <textarea className="w-full min-h-[60px] rounded-md border border-border bg-surface px-3 py-2 text-sm" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} />
          <Button label={saving ? "Enregistrement…" : "Enregistrer"} intent="primary" size="sm" state={saving ? "loading" : "default"} onClick={handleSubmit} />
        </div>
      </div>
    </Modal>
  );
}
